import { Splide } from '@hh/global.js'

class QuickBuyModal extends HTMLElement {
  connectedCallback() {
    this.productHandle = this.getAttribute('product-handle')
    this.contentRequestId = 0
    this.fetchAbortController = null
    this.initHandleObserver()

    if (this.productHandle) {
      this.updateContent()
    }
  }

  disconnectedCallback() {
    this.cancelContentFetch()
    this.innerHTML = ''
    this.productHandle = null
    this.handleObserver?.disconnect()
  }

  cancelContentFetch() {
    this.fetchAbortController?.abort()
    this.fetchAbortController = null
  }

  initHandleObserver() {
    this.handleObserver = new MutationObserver(() => {
      const updatedProductHandle = this.getAttribute('product-handle')
      if (updatedProductHandle !== this.productHandle) {
        this.productHandle = updatedProductHandle
        this.updateContent()
      }
    })

    this.handleObserver.observe(this, {
      attributes: true,
      attributeFilter: ['product-handle'],
      subtree: false,
    })
  }

  setProductHandle(productHandle, options = {}) {
    if (!productHandle) {
      return Promise.resolve(false)
    }

    this.productHandle = productHandle

    if (this.getAttribute('product-handle') !== productHandle) {
      this.setAttribute('product-handle', productHandle)
    }

    return this.updateContent(options)
  }

  handleLoadFailure(
    requestId,
    {
      shouldCloseModal = true,
      preserveContent = false,
      showErrorOnFailure = false,
    } = {}
  ) {
    if (requestId !== this.contentRequestId) {
      return
    }

    if (showErrorOnFailure) {
      this.showLoadError(requestId)
      return
    }

    if (!preserveContent) {
      this.clearContent(requestId)
    }

    if (shouldCloseModal) {
      QuickBuyModalController.close()
    }
  }

  showLoadError(requestId) {
    if (requestId !== this.contentRequestId) {
      return
    }

    const message =
      this.getAttribute('data-load-error') ||
      'Unable to load product. Please try again.'

    this.innerHTML = ''
    const errorEl = document.createElement('div')
    errorEl.className = 'quick-buy-modal__error'
    errorEl.setAttribute('role', 'alert')
    const messageEl = document.createElement('p')
    messageEl.className = 'quick-buy-modal__error-message'
    messageEl.textContent = message
    errorEl.append(messageEl)
    this.append(errorEl)
    QuickBuyModalController.revealContent()
  }

  clearContent(requestId) {
    if (requestId === undefined || requestId === this.contentRequestId) {
      this.innerHTML = ''
    }
  }

  async updateContent({
    shouldCloseOnFailure = true,
    preserveContent = false,
    showErrorOnFailure = false,
  } = {}) {
    const productHandle = this.getAttribute('product-handle')

    if (!productHandle) {
      this.clearContent()
      return false
    }

    this.cancelContentFetch()

    const requestId = ++this.contentRequestId
    const abortController = new AbortController()
    this.fetchAbortController = abortController

    this.productHandle = productHandle

    if (!preserveContent) {
      this.clearContent(requestId)
    }

    this.classList.add('loading')
    this.setAttribute('aria-busy', 'true')

    await Promise.resolve()

    if (requestId !== this.contentRequestId) {
      return false
    }

    try {
      const data = await QuickBuyModalController.fetchSectionHtml(
        productHandle,
        abortController.signal
      )

      if (requestId !== this.contentRequestId) {
        return false
      }

      if (!data) {
        this.handleLoadFailure(requestId, {
          shouldCloseModal: shouldCloseOnFailure,
          preserveContent,
          showErrorOnFailure,
        })
        return false
      }

      const contentHtml = QuickBuyModalController.parseContentHtml(data)

      if (requestId !== this.contentRequestId) {
        return false
      }

      if (!contentHtml) {
        this.handleLoadFailure(requestId, {
          shouldCloseModal: shouldCloseOnFailure,
          preserveContent,
          showErrorOnFailure,
        })
        return false
      }

      this.innerHTML = contentHtml
      QuickBuyModalController.revealContent()
      return true
    } catch (error) {
      if (error.name === 'AbortError') {
        return false
      }

      console.error(error)
      this.handleLoadFailure(requestId, {
        shouldCloseModal: shouldCloseOnFailure,
        preserveContent,
        showErrorOnFailure,
      })
      return false
    } finally {
      if (requestId === this.contentRequestId) {
        this.classList.remove('loading')
        this.removeAttribute('aria-busy')
        this.fetchAbortController = null
      }
    }
  }
}

if (!customElements.get('quick-buy-modal')) {
  customElements.define('quick-buy-modal', QuickBuyModal)
}

class QuickBuyModalContent extends HTMLElement {
  connectedCallback() {
    this.parentModal = this.closest('quick-buy-modal')
    this.abortController = new AbortController()

    this.initApps()
    this.initVariantsList()
    this.initMediaCarousel()
    this.initProductAddedClose()
  }

  disconnectedCallback() {
    this.parentModal = null
    this.abortController?.abort()
    this.teardownMediaCarousel()
  }

  teardownMediaCarousel() {
    this.carouselAbortController?.abort()
    this.carouselAbortController = null
    this.mediaSplide?.destroy()
    this.mediaSplide = null
  }

  initApps() {
    if (typeof window.yotpo?.initWidgets === 'function') {
      window.yotpo?.initWidgets()
    }
  }

  initVariantsList() {
    const variantButtons = this.querySelectorAll('[data-variant-product-handle]')
    variantButtons.forEach((button) => {
      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault()
          event.stopPropagation()

          const variantProductHandle = button.getAttribute(
            'data-variant-product-handle'
          )
          this.parentModal?.setProductHandle(variantProductHandle, {
            shouldCloseOnFailure: false,
            preserveContent: true,
          })
        },
        { signal: this.abortController.signal }
      )
    })
  }

  initMediaCarousel() {
    const carouselEl = this.querySelector('[data-quick-buy-modal-media-carousel]')
    if (!carouselEl) {
      return
    }

    const slideCount = carouselEl.querySelectorAll(
      '.splide__list > .splide__slide'
    ).length
    const hasMultipleSlides = slideCount > 1

    this.teardownMediaCarousel()

    this.mediaSplide = new Splide(carouselEl, {
      arrows: false,
      pagination: false,
      rewind: true,
      mediaQuery: 'min',
      type: hasMultipleSlides ? 'fade' : 'slide',
      breakpoints: {
        768: {
          arrows: hasMultipleSlides,
        },
      },
    })

    const progressBar = carouselEl.querySelector(
      '[data-quick-buy-modal-media-progress-bar]'
    )
    if (progressBar) {
      const updateProgressBar = () => {
        const totalItems = this.mediaSplide.length - 1
        const position =
          totalItems > 0 ? (this.mediaSplide.index / totalItems) * 100 : 0

        progressBar.style.setProperty(
          '--quick-buy-modal-progress-bar-position',
          `${position}%`
        )
      }

      this.mediaSplide.on('mounted move', updateProgressBar)
    }

    this.mediaSplide.mount()
  }

  refreshMediaCarousel() {
    this.initMediaCarousel()
  }

  initProductAddedClose() {
    this.addEventListener(
      'productAdded',
      () => {
        QuickBuyModalController.close()
      },
      { signal: this.abortController.signal }
    )
  }
}

if (!customElements.get('quick-buy-modal-content')) {
  customElements.define('quick-buy-modal-content', QuickBuyModalContent)
}

class QuickBuyModalController {
  static MODAL_ID = 'quick-buy-modal'
  static SECTION_ID = 'quick-buy-modal-content'
  static closeTimerId = null
  static closeScrollLockActive = false
  static prefetchRequests = new Map()

  static cancelClose() {
    if (QuickBuyModalController.closeTimerId != null) {
      window.clearTimeout(QuickBuyModalController.closeTimerId)
      QuickBuyModalController.closeTimerId = null
    }

    QuickBuyModalController.unlockCloseScrollLock()
  }

  static isShellOpening(shellModal) {
    return shellModal?.dataset.shellOpening === 'true'
  }

  static isShellClosing(shellModal) {
    return shellModal?.dataset.shellClosing === 'true'
  }

  static cancelPendingOpen() {
    const shellModal = QuickBuyModalController.getShellModal()
    const contentHost = QuickBuyModalController.getContentHost()

    if (!shellModal || !QuickBuyModalController.isShellOpening(shellModal)) {
      return
    }

    delete shellModal.dataset.shellOpening
    shellModal.animateOpen = false
    contentHost?.cancelContentFetch()

    if (contentHost) {
      contentHost.classList.remove('loading')
      contentHost.removeAttribute('aria-busy')
    }

    QuickBuyModalController.unlockCloseScrollLock()
  }

  static lockBodyScrollForClose() {
    QuickBuyModalController.closeScrollLockActive = true
    QuickBuyModalController.lockBodyScroll()
  }

  static unlockCloseScrollLock() {
    if (!QuickBuyModalController.closeScrollLockActive) {
      return
    }

    QuickBuyModalController.closeScrollLockActive = false
    QuickBuyModalController.unlockBodyScroll()
  }

  static lockBodyScroll() {
    document.documentElement.classList.add('body-lock')
    document.body.classList.add('body-lock')
  }

  static hasOpenScrollLockedModal() {
    return [...document.querySelectorAll('hh-modal[custom-lock-scroll]')].some(
      (modal) => modal.open === true || modal.hasAttribute('open')
    )
  }

  static unlockBodyScroll() {
    if (QuickBuyModalController.hasOpenScrollLockedModal()) {
      return
    }

    document.documentElement.classList.remove('body-lock')
    document.body.classList.remove('body-lock')
  }

  static init() {
    if (QuickBuyModalController.initialized) {
      return
    }

    document.addEventListener('click', QuickBuyModalController.handleTriggerClick)
    document.addEventListener(
      'click',
      QuickBuyModalController.handleCloseClick,
      true
    )
    document.addEventListener(
      'keydown',
      QuickBuyModalController.handleCloseKeydown,
      true
    )

    QuickBuyModalController.initialized = true
    QuickBuyModalController.prefetchPageContent()
  }

  static parseContentHtml(data) {
    const responseHtml = new DOMParser().parseFromString(data, 'text/html')
    return (
      responseHtml.querySelector('[data-quick-buy-modal-content]')?.outerHTML ??
      null
    )
  }

  static fetchSectionHtml(productHandle, signal) {
    return fetch(
      `/products/${productHandle}?section_id=${QuickBuyModalController.SECTION_ID}`,
      {
        method: 'GET',
        cache: 'no-cache',
        signal,
      }
    ).then(async (response) => {
      if (!response.ok) {
        return null
      }

      return response.text()
    })
  }

  static prefetchContent(productHandle) {
    if (!productHandle) {
      return Promise.resolve()
    }

    const inFlight = QuickBuyModalController.prefetchRequests.get(productHandle)
    if (inFlight) {
      return inFlight
    }

    const request = QuickBuyModalController.fetchSectionHtml(productHandle)
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        QuickBuyModalController.prefetchRequests.delete(productHandle)
      })

    QuickBuyModalController.prefetchRequests.set(productHandle, request)
    return request
  }

  static getTriggerHandles() {
    const handles = new Set()

    document.querySelectorAll('[data-quick-buy-modal-trigger]').forEach((trigger) => {
      const handle =
        trigger.dataset.productHandle ||
        trigger.getAttribute('data-product-handle')

      if (handle) {
        handles.add(handle)
      }
    })

    return [...handles]
  }

  static prefetchPageContent() {
    const handles = QuickBuyModalController.getTriggerHandles()
    if (handles.length === 0) {
      return
    }

    const preload = () => {
      void Promise.all(
        handles.map((handle) => QuickBuyModalController.prefetchContent(handle))
      )
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        void preload()
      }, { timeout: 3000 })
    } else {
      window.setTimeout(() => {
        void preload()
      }, 0)
    }
  }

  static handleCloseClick(event) {
    const closeTrigger = event.target.closest('[data-quick-buy-modal-close]')
    if (!closeTrigger) {
      return
    }

    const shellModal = QuickBuyModalController.getShellModal()
    if (!shellModal?.open) {
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
    QuickBuyModalController.close()
  }

  static handleCloseKeydown(event) {
    if (event.key !== 'Escape') {
      return
    }

    const shellModal = QuickBuyModalController.getShellModal()
    if (!shellModal?.open && !QuickBuyModalController.isShellOpening(shellModal)) {
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
    QuickBuyModalController.close()
  }

  static getTransitionDurationMs(shellModal) {
    const value =
      getComputedStyle(shellModal).getPropertyValue('--modal-transition-duration').trim() ||
      '0.4s'

    if (value.endsWith('ms')) {
      return parseFloat(value)
    }

    if (value.endsWith('s')) {
      return parseFloat(value) * 1000
    }

    return 400
  }

  static showShellBackdrop(shellModal) {
    const bg = shellModal?.querySelector('.quick-buy-modal-shell__bg')
    if (!bg) {
      return
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bg.classList.add('is-visible')
      })
    })
  }

  static hideShellBackdrop(shellModal) {
    shellModal
      ?.querySelector('.quick-buy-modal-shell__bg')
      ?.classList.remove('is-visible')
  }

  static handleTriggerClick(event) {
    const trigger = event.target.closest('[data-quick-buy-modal-trigger]')
    if (!trigger) {
      return
    }

    const productHandle =
      trigger.dataset.productHandle || trigger.getAttribute('data-product-handle')
    if (!productHandle) {
      return
    }

    const variant =
      trigger.dataset.quickBuyModalVariant ||
      trigger.getAttribute('data-quick-buy-modal-variant')

    const overlayOpacity =
      trigger.dataset.quickBuyModalOverlayOpacity ||
      trigger.getAttribute('data-quick-buy-modal-overlay-opacity')

    event.preventDefault()

    const shellModal = QuickBuyModalController.getShellModal()
    if (QuickBuyModalController.isShellClosing(shellModal)) {
      return
    }

    const contentHost = QuickBuyModalController.getContentHost()
    if (!contentHost) {
      return
    }

    QuickBuyModalController.open({ variant, overlayOpacity })

    const currentHandle = contentHost.getAttribute('product-handle')
    if (currentHandle !== productHandle) {
      contentHost.innerHTML = ''
    }

    contentHost.classList.add('loading')
    contentHost.setAttribute('aria-busy', 'true')

    void contentHost.setProductHandle(productHandle, {
      shouldCloseOnFailure: false,
      showErrorOnFailure: true,
      preserveContent: currentHandle === productHandle,
    })
  }

  static getShellModal() {
    return document.getElementById(QuickBuyModalController.MODAL_ID)
  }

  static getContentHost() {
    return document.querySelector('quick-buy-modal.quick-buy-modal')
  }

  static open({ variant, overlayOpacity } = {}) {
    const shellModal = QuickBuyModalController.getShellModal()
    if (!shellModal) {
      return
    }

    if (QuickBuyModalController.isShellClosing(shellModal)) {
      return
    }

    QuickBuyModalController.cancelClose()
    delete shellModal.dataset.shellClosing

    shellModal.classList.toggle(
      'quick-buy-modal-shell--quick-shop-2026',
      variant === 'quick-shop-2026'
    )

    if (variant === 'quick-shop-2026' && overlayOpacity != null && overlayOpacity !== '') {
      const bgOpacity = Math.min(Math.max(Number(overlayOpacity) / 100, 0), 1)
      shellModal.style.setProperty('--quick-buy-modal-bg-opacity', bgOpacity)
    } else {
      shellModal.style.removeProperty('--quick-buy-modal-bg-opacity')
    }

    shellModal.dataset.shellOpening = 'true'
    shellModal.animateOpen = false
  }

  static refreshVisibleContent() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const contentEl = QuickBuyModalController.getContentHost()?.querySelector(
          'quick-buy-modal-content'
        )

        contentEl?.refreshMediaCarousel()
      })
    })
  }

  static revealContent() {
    const shellModal = QuickBuyModalController.getShellModal()
    if (!shellModal) {
      return
    }

    if (
      !QuickBuyModalController.isShellOpening(shellModal) &&
      !shellModal.open
    ) {
      return
    }

    delete shellModal.dataset.shellOpening
    shellModal.open = true

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        shellModal
          .querySelector('.quick-buy-modal-shell__bg')
          ?.classList.add('is-visible')
        shellModal.animateOpen = true
        QuickBuyModalController.refreshVisibleContent()
      })
    })
  }

  static close() {
    const shellModal = QuickBuyModalController.getShellModal()
    if (!shellModal || shellModal.dataset.shellClosing === 'true') {
      return
    }

    if (QuickBuyModalController.isShellOpening(shellModal)) {
      QuickBuyModalController.cancelPendingOpen()
      return
    }

    if (!shellModal.open) {
      return
    }

    QuickBuyModalController.cancelClose()

    shellModal.dataset.shellClosing = 'true'
    shellModal.animateOpen = false
    shellModal.open = false
    QuickBuyModalController.lockBodyScrollForClose()
    QuickBuyModalController.hideShellBackdrop(shellModal)

    QuickBuyModalController.closeTimerId = window.setTimeout(() => {
      QuickBuyModalController.closeTimerId = null

      if (shellModal.dataset.shellClosing !== 'true') {
        return
      }

      delete shellModal.dataset.shellClosing
      shellModal.classList.remove('quick-buy-modal-shell--quick-shop-2026')
      shellModal.style.removeProperty('--quick-buy-modal-bg-opacity')
      shellModal.animateOpen = false
      QuickBuyModalController.unlockCloseScrollLock()
    }, QuickBuyModalController.getTransitionDurationMs(shellModal))
  }
}

QuickBuyModalController.init()
